import React from "react";
import axios from "axios";
import { Card, CardContent } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Package, Truck, BarChart3, Share2, Edit3, Trash2, Upload, ExternalLink, RefreshCw, Send } from "lucide-react";
import { TelegramPreviewModal } from "./TelegramPreviewModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const TelegramPostButton = ({ article }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        className="bg-sky-50 hover:bg-sky-100 text-sky-700 border-sky-200"
        title="Anteprima e pubblicazione sul canale Telegram"
        data-testid={`article-telegram-post-${article.id}`}
      >
        <Send className="w-4 h-4 mr-1" />
        Canale
      </Button>
      {open && <TelegramPreviewModal article={article} onClose={() => setOpen(false)} />}
    </>
  );
};

const TelegramScheduleButton = ({ article }) => {
  const [state, setState] = React.useState("idle");
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={state === "loading"}
      onClick={async () => {
        const d = new Date(Date.now() + 60 * 60 * 1000);
        d.setSeconds(0, 0);
        const pad = (n) => String(n).padStart(2, "0");
        const suggested = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        const input = window.prompt(`Quando pubblicare "${article.title}" sul canale?\nFormato: AAAA-MM-GG HH:MM (ora italiana)`, suggested);
        if (!input) return;
        const when = new Date(input.trim().replace(" ", "T"));
        if (isNaN(when.getTime()) || when.getTime() < Date.now() + 60000) {
          alert("Data/ora non valida o già passata");
          return;
        }
        setState("loading");
        try {
          const token = sessionStorage.getItem("access_token");
          const fd = new FormData();
          fd.append("article_id", article.id);
          fd.append("scheduled_at", when.toISOString());
          await axios.post(`${BACKEND_URL}/api/admin/telegram/scheduled-posts`, fd, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setState("done");
          setTimeout(() => setState("idle"), 4000);
        } catch (e) {
          alert(e.response?.data?.detail || "Programmazione fallita");
          setState("idle");
        }
      }}
      className="bg-sky-50 hover:bg-sky-100 text-sky-700 border-sky-200 px-2"
      title="Programma la pubblicazione sul canale Telegram"
      data-testid={`article-telegram-schedule-${article.id}`}
    >
      {state === "loading" ? "…" : state === "done" ? "✅ In programma" : "⏰"}
    </Button>
  );
};

/**
 * Articles grid — extracted from AdminApp.jsx during refactor.
 * Presentational only: receives articles + action callbacks.
 */
export const ArticlesGrid = ({
  articles,
  backendUrl,
  formatPrice,
  getIvaLabel,
  onAnalytics,
  onEdit,
  onDelete,
  onShare,
  onPublishEbay,
  onEndEbay,
  onReconcileEbay,
  ebayLoadingId,
  onPublishSocial,
  socialLoadingId,
}) => {
  if (articles.length === 0) {
    return (
      <div className="text-center py-12" data-testid="articles-empty">
        <div className="text-gray-400 mb-4">
          <Package className="w-24 h-24 mx-auto" />
        </div>
        <h3 className="text-xl font-medium text-gray-900 mb-2">Magazzino vuoto</h3>
        <p className="text-gray-600 mb-6">Inizia aggiungendo il tuo primo articolo!</p>
      </div>
    );
  }

  const renderEbayBadge = (article) => {
    const ebay = article.ebay;
    if (!ebay || !ebay.status) return null;
    const colors = {
      active: "bg-green-600 text-white",
      ended: "bg-gray-500 text-white",
      error: "bg-red-600 text-white",
      draft: "bg-amber-500 text-white",
    };
    const label = {
      active: "🟦 su eBay",
      ended: "eBay ritirato",
      error: "eBay errore",
      draft: "eBay bozza",
    };
    return (
      <Badge className={`text-[10px] ${colors[ebay.status] || "bg-gray-400 text-white"}`}>
        {label[ebay.status] || ebay.status}
      </Badge>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
      {articles.map((article) => {
        const ebay = article.ebay || {};
        const isActive = ebay.status === "active";
        const isLoading = ebayLoadingId === article.id;

        return (
          <Card key={article.id} className="hover:shadow-lg transition-shadow duration-200">
            {(article.images_url?.[0] || article.photo_url) && (
              <div className="aspect-video w-full overflow-hidden rounded-t-lg">
                <img
                  src={article.images_url?.[0] || `${backendUrl}${article.photo_url}`}
                  alt={article.title}
                  className="w-full h-full object-contain hover:scale-105 transition-transform duration-200 p-1"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}
            <CardContent className="p-4">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate mb-1">{article.title}</h3>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">{article.sku}</p>
                  {renderEbayBadge(article)}
                </div>
                <span className="text-lg font-bold text-green-600 ml-2">{formatPrice(article.price)}</span>
              </div>

              <p className="text-sm text-gray-600 mb-3 line-clamp-2">{article.description}</p>

              <div className="space-y-2 text-xs text-gray-600 mb-4">
                <div className="flex justify-between">
                  <span>IVA: {getIvaLabel(article.iva_percentage)}</span>
                  <span>Stock: {article.quantity_stock}</span>
                </div>
                <div className="flex justify-between">
                  <span>Categoria: {article.category}</span>
                  <span>Peso: {article.weight}g</span>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center space-x-1">
                    <Truck className="w-3 h-3" />
                    <span>ITA: {formatPrice(article.shipping_cost_national)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Truck className="w-3 h-3" />
                    <span>EST: {formatPrice(article.shipping_cost_international)}</span>
                  </div>
                </div>
                {article.ebay_price && (
                  <div className="text-amber-700">
                    🟦 Prezzo eBay: {formatPrice(article.ebay_price)}
                  </div>
                )}
              </div>

              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAnalytics(article)}
                  className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                  data-testid={`article-stats-${article.id}`}
                >
                  <BarChart3 className="w-4 h-4 mr-1" />
                  Stats
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => onShare(article)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid={`article-share-${article.id}`}
                >
                  <Share2 className="w-4 h-4 mr-1" />
                  Condividi
                </Button>
                <TelegramPostButton article={article} />
                <TelegramScheduleButton article={article} />
                {/* eBay publish / view / end buttons */}
                {onPublishEbay && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPublishEbay(article)}
                    disabled={isLoading}
                    className={isActive
                      ? "bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                      : "bg-amber-50 hover:bg-amber-100 text-amber-700 border-amber-300"
                    }
                    data-testid={`article-ebay-publish-${article.id}`}
                    title={isActive ? "Aggiorna listing eBay" : "Pubblica su eBay"}
                  >
                    {isLoading ? (
                      <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-1" />
                    )}
                    {isActive ? "Aggiorna eBay" : "🟦 Pubblica eBay"}
                  </Button>
                )}
                {isActive && ebay.listing_id && (
                  <a
                    href={`https://www.ebay.it/itm/${ebay.listing_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-8 px-2 border border-blue-300 rounded text-blue-700 bg-blue-50 hover:bg-blue-100 text-xs"
                    title="Apri listing su eBay"
                    data-testid={`article-ebay-view-${article.id}`}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Vedi su eBay
                  </a>
                )}
                {isActive && onReconcileEbay && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onReconcileEbay(article)}
                    disabled={isLoading}
                    className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-300"
                    data-testid={`article-ebay-reconcile-${article.id}`}
                    title="Riallinea stock locale con quantità attuale su eBay (se la sync ha saltato una vendita)"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Riallinea Stock
                  </Button>
                )}
                {isActive && onEndEbay && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEndEbay(article)}
                    disabled={isLoading}
                    className="bg-red-50 hover:bg-red-100 text-red-700 border-red-300"
                    data-testid={`article-ebay-end-${article.id}`}
                    title="Ritira listing da eBay"
                  >
                    ✕ eBay
                  </Button>
                )}
                {/* Social publish button (FB + IG) */}
                {onPublishSocial && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPublishSocial(article)}
                    disabled={socialLoadingId === article.id}
                    className="bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-300"
                    data-testid={`article-social-publish-${article.id}`}
                    title="Pubblica su Facebook + Instagram"
                  >
                    {socialLoadingId === article.id ? (
                      <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-1" />
                    )}
                    📲 FB + IG
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onEdit(article)}
                  data-testid={`article-edit-${article.id}`}
                >
                  <Edit3 className="w-4 h-4 mr-1" />
                  Modifica
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onDelete(article.id)}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid={`article-delete-${article.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Elimina
                </Button>
              </div>
              {ebay.last_error && (
                <p className="text-xs text-red-600 mt-2 truncate" title={ebay.last_error}>
                  ⚠️ {ebay.last_error}
                </p>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
